import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
    Chip,
    Switch,
    FormControlLabel,
    Tab,
    Tabs,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    ToggleButtonGroup,
    ToggleButton,
    LinearProgress,
    List,
    ListItem,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    WarningAmber as WarningAmberIcon,
    ExpandMore as ExpandMoreIcon,
    GridOn as GridOnIcon,
} from '@mui/icons-material';
import { PageHeader, KontrolaDialog } from '../../components/admin';
import EmailNotifikaceTab from '../../components/admin/kontroly/EmailNotifikaceTab';
import DokumentyTab from '../../components/admin/kontroly/DokumentyTab';
import NahrazujeKontrolyPole from '../../components/admin/kontroly/NahrazujeKontrolyPole';
import { maPushUpozorneni, parsujIntervaly } from '../../components/admin/kontroly/upozorneniTypu';
import NahledTypKontrolyPanel from '../../components/admin/kontroly/NahledTypKontrolyPanel';
import SmazatTypKontrolyDialog from '../../components/admin/kontroly/SmazatTypKontrolyDialog';
import KontrolaStavTooltip from '../../components/admin/kontroly/KontrolaStavTooltip';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { csCZ } from '@mui/x-data-grid/locales';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { type Dayjs } from 'dayjs';
import type {
    TypKontroly,
    TypKontrolyRequest,
    MetadataFieldSchema,
    NahrazeniRequest,
    NahledTypKontrolyRequest,
} from '../../api/JsdhNotifikaceService/generated/models/TypKontroly';
import type {
    KontrolaStatusResponse,
    UzivatelKontrolyRow,
} from '../../api/JsdhNotifikaceService/generated/models/Kontrola';
import {
    useTypyKontrolQuery,
    useKontrolyOverviewQuery,
    useCreateTypKontrolyMutation,
    useUpdateTypKontrolyMutation,
    useNahledTypKontrolyQuery,
} from '../../api/JsdhNotifikaceService/service/KontrolyMutations';
import { useAllSpecializaceQuery } from '../../api/JsdhNotifikaceService/service/SpecializaceMutations';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';

// ==========================================
// HELPER: Status Chip
// ==========================================

const StatusChip: React.FC<{ status: KontrolaStatusResponse }> = ({ status }) => {
    if (!status.relevantni) {
        return <Typography variant="body2" color="text.disabled">—</Typography>;
    }

    switch (status.status) {
        case 'PLATNA':
            return (
                <Chip
                    color="success"
                    label={`Platná do ${dayjs(status.datumExpirace).format('DD.MM.YYYY')}`}
                    size="small"
                />
            );
        case 'BLIZI_SE_EXPIRACE':
            return (
                <Chip
                    color="warning"
                    label={`Vyprší ${dayjs(status.datumExpirace).format('DD.MM.YYYY')}`}
                    size="small"
                />
            );
        case 'EXPIROVANA':
            return (
                <Chip
                    color="error"
                    label={`Expirovaná ${dayjs(status.datumExpirace).format('DD.MM.YYYY')}`}
                    size="small"
                />
            );
        case 'CHYBI':
            return <Chip variant="outlined" label="Chybí" size="small" />;
        default:
            return null;
    }
};

// ==========================================
// TAB 1: Přehled (matice)
// ==========================================

const PrehledTab: React.FC = () => {
    const navigate = useNavigate();
    const { data: overview, isLoading, error } = useKontrolyOverviewQuery();
    const [filterProblems, setFilterProblems] = useState(false);
    const [kontrolyView, setKontrolyView] = useState<'problems' | 'matrix'>('problems');
    const [searchQuery, setSearchQuery] = useState('');
    const [dialogState, setDialogState] = useState<{
        open: boolean;
        uzivatelId: number;
        uzivatelJmeno: string;
        kontrolaStatus: KontrolaStatusResponse;
    } | null>(null);

    // Compliance summary: count relevant + valid per typ (must be before early returns)
    const complianceSummary = useMemo(() => {
        if (!overview) return [];
        return overview.typyKontrol.map((typ) => {
            let relevant = 0;
            let valid = 0;
            for (const u of overview.uzivatele) {
                const k = u.kontroly.find((k) => k.typKontrolyId === typ.id);
                if (k && k.relevantni) {
                    relevant++;
                    if (k.status === 'PLATNA') valid++;
                }
            }
            return { typId: typ.id, nazev: typ.nazev, valid, relevant };
        }).filter((s) => s.relevant > 0);
    }, [overview]);

    if (isLoading) return <CircularProgress />;
    if (error) return <Alert severity="error">Nepodařilo se načíst přehled kontrol.</Alert>;
    if (!overview) return null;

    const filteredRows = filterProblems
        ? overview.uzivatele.filter((u) =>
            u.kontroly.some((k) =>
                k.relevantni && (k.status === 'EXPIROVANA' || k.status === 'BLIZI_SE_EXPIRACE' || k.status === 'CHYBI')
            )
        )
        : overview.uzivatele;

    const columns: GridColDef[] = [
        {
            field: 'jmeno',
            headerName: 'Jmeno',
            width: 180,
            renderCell: (params) => (
                <Box
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/admin/users/${params.row.uzivatelId}`)}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'primary.main',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        {params.row.jmeno}
                    </Typography>
                </Box>
            ),
        },
        ...overview.typyKontrol.map((typ) => ({
            field: `typ_${typ.id}`,
            headerName: typ.nazev.length > 18 ? typ.nazev.substring(0, 16) + '...' : typ.nazev,
            width: 180,
            sortable: false,
            align: 'center' as const,
            headerAlign: 'center' as const,
            renderHeader: () => (
                <Tooltip title={typ.nazev} arrow>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 500, maxWidth: 160 }}>
                        {typ.nazev}
                    </Typography>
                </Tooltip>
            ),
            renderCell: (params: any) => {
                const row = params.row as UzivatelKontrolyRow;
                const kontrola = row.kontroly.find((k) => k.typKontrolyId === typ.id);
                if (!kontrola) return null;

                const textColor = !kontrola.relevantni ? 'text.disabled'
                    : kontrola.status === 'PLATNA' ? 'success.main'
                    : kontrola.status === 'BLIZI_SE_EXPIRACE' ? 'warning.main'
                    : kontrola.status === 'EXPIROVANA' ? 'error.main'
                    : 'text.secondary';
                const label = !kontrola.relevantni ? ''
                    : kontrola.status === 'PLATNA' ? `✓ ${dayjs(kontrola.datumExpirace).format('DD.MM.YY')}`
                    : kontrola.status === 'BLIZI_SE_EXPIRACE' ? `⚠ ${dayjs(kontrola.datumExpirace).format('DD.MM.YY')}`
                    : kontrola.status === 'EXPIROVANA' ? `✗ ${dayjs(kontrola.datumExpirace).format('DD.MM.YY')}`
                    : '✗';

                return (
                    <KontrolaStavTooltip kontrola={kontrola}>
                    <Box
                        sx={{
                            cursor: kontrola.relevantni ? 'pointer' : 'default',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            height: '100%', width: '100%',
                            color: textColor,
                            fontSize: '0.875rem',
                            fontWeight: 600,
                        }}
                        onClick={() => {
                            if (kontrola.relevantni) {
                                setDialogState({
                                    open: true,
                                    uzivatelId: row.uzivatelId,
                                    uzivatelJmeno: row.jmeno,
                                    kontrolaStatus: kontrola,
                                });
                            }
                        }}
                    >
                        {label}
                    </Box>
                    </KontrolaStavTooltip>
                );
            },
        })),
    ];

    const rows = filteredRows.map((u) => ({
        id: u.uzivatelId,
        ...u,
    }));

    return (
        <Box>
            {/* Compliance summary chips */}
            {complianceSummary.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    {complianceSummary.map((s) => {
                        const ratio = s.relevant > 0 ? s.valid / s.relevant : 0;
                        const chipColor: 'success' | 'warning' | 'error' =
                            ratio >= 1 ? 'success' : ratio >= 0.5 ? 'warning' : 'error';
                        return (
                            <Chip
                                key={s.typId}
                                label={`${s.nazev}: ${s.valid}/${s.relevant} ${ratio >= 1 ? '\u2713' : '\u2717'}`}
                                color={chipColor}
                                variant="outlined"
                                size="small"
                            />
                        );
                    })}
                </Box>
            )}

            {/* Search + View toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                {kontrolyView === 'problems' ? (
                    <TextField
                        placeholder="Hledat podle jména..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        size="small"
                        sx={{ flex: 1, maxWidth: { xs: '100%', sm: 300 } }}
                    />
                ) : (
                    <FormControlLabel
                        control={<Switch checked={filterProblems} onChange={(e) => setFilterProblems(e.target.checked)} size="small" />}
                        label={<Typography variant="body2">Jen problémové</Typography>}
                        sx={{ m: 0 }}
                    />
                )}
                <ToggleButtonGroup
                    value={kontrolyView}
                    exclusive
                    onChange={(_e, val) => { if (val) setKontrolyView(val); }}
                    size="small"
                >
                    <ToggleButton value="problems">
                        <WarningAmberIcon sx={{ mr: 0.5 }} fontSize="small" />Přehled
                    </ToggleButton>
                    <ToggleButton value="matrix">
                        <GridOnIcon sx={{ mr: 0.5 }} fontSize="small" />Matice
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Problems view */}
            {kontrolyView === 'problems' && (
                <Box>
                    {overview.typyKontrol.map((typ) => {
                        const relevantUsers = overview.uzivatele
                            .map((u) => {
                                const kontrola = u.kontroly.find((k) => k.typKontrolyId === typ.id);
                                if (!kontrola || !kontrola.relevantni) return null;
                                return {
                                    uzivatelId: u.uzivatelId,
                                    jmeno: u.jmeno,
                                    funkce: u.funkce?.join(', ') || '',
                                    status: kontrola.status,
                                    datumExpirace: kontrola.datumExpirace,
                                    kontrolaStatus: kontrola,
                                };
                            })
                            .filter(Boolean) as {
                                uzivatelId: number;
                                jmeno: string;
                                funkce: string;
                                status: string;
                                datumExpirace?: string;
                                kontrolaStatus: KontrolaStatusResponse;
                            }[];

                        const filteredBySearch = searchQuery.trim()
                            ? relevantUsers.filter((u) => u.jmeno.toLowerCase().includes(searchQuery.toLowerCase()))
                            : relevantUsers;
                        const relevantCount = relevantUsers.length;
                        const validCount = relevantUsers.filter((u) => u.status === 'PLATNA').length;

                        return (
                            <Accordion
                                key={typ.id}
                                defaultExpanded={false}
                                variant="outlined"
                                sx={{ mb: 1, '&:before': { display: 'none' }, overflow: 'hidden' }}
                                disableGutters
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{ bgcolor: 'action.hover', minHeight: 48, '& .MuiAccordionSummary-content': { alignItems: 'center', justifyContent: 'space-between', mr: 1 } }}
                                >
                                    <Typography variant="subtitle1" fontWeight={700}>{typ.nazev}</Typography>
                                    <Chip
                                        label={`${validCount}/${relevantCount} splněno`}
                                        color={relevantCount === 0 || validCount === relevantCount ? 'success' : validCount / relevantCount >= 0.5 ? 'warning' : 'error'}
                                        size="small"
                                    />
                                </AccordionSummary>
                                <AccordionDetails sx={{ p: 0 }}>
                                <LinearProgress
                                    variant="determinate"
                                    value={relevantCount > 0 ? (validCount / relevantCount) * 100 : 100}
                                    color={relevantCount === 0 || validCount === relevantCount ? 'success' : validCount / relevantCount >= 0.5 ? 'warning' : 'error'}
                                    sx={{ height: 3 }}
                                />
                                {/* Member list */}
                                {relevantCount > 0 ? (
                                    <List disablePadding>
                                        {filteredBySearch
                                            .sort((a, b) => {
                                                const statusOrder: Record<string, number> = { PLATNA: 0, BLIZI_SE_EXPIRACE: 1, EXPIROVANA: 2, CHYBI: 3 };
                                                return (statusOrder[a.status] || 4) - (statusOrder[b.status] || 4);
                                            })
                                            .map((item) => (
                                                <ListItem
                                                    key={item.uzivatelId}
                                                    divider
                                                    sx={{ py: 1, px: 2 }}
                                                >
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {item.jmeno}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mt: 0.5 }}>
                                                            <StatusChip status={item.kontrolaStatus} />
                                                            {item.status !== 'PLATNA' && item.kontrolaStatus.relevantni && (
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    onClick={() => setDialogState({
                                                                        open: true,
                                                                        uzivatelId: item.uzivatelId,
                                                                        uzivatelJmeno: item.jmeno,
                                                                        kontrolaStatus: item.kontrolaStatus,
                                                                    })}
                                                                >
                                                                    Zaznamenat
                                                                </Button>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </ListItem>
                                            ))}
                                    </List>
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
                                        Žádný člen nemá tuto povinnost
                                    </Typography>
                                )}
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </Box>
            )}

            {/* Matrix view */}
            {kontrolyView === 'matrix' && (
                <>
                    <Box sx={{ overflowX: 'auto' }}>
                        <Paper>
                            <DataGrid
                                rows={rows}
                                columns={columns}
                                localeText={csCZ.components.MuiDataGrid.defaultProps.localeText}
                                autoHeight
                                disableRowSelectionOnClick
                                pageSizeOptions={[25, 50, 100]}
                                initialState={{
                                    pagination: { paginationModel: { pageSize: 25 } },
                                }}
                                getRowHeight={() => 'auto'}
                                sx={{
                                    '& .MuiDataGrid-cell': {
                                        py: 1,
                                    },
                                }}
                            />
                        </Paper>
                    </Box>
                </>
            )}

            {dialogState && (
                <KontrolaDialog
                    open={dialogState.open}
                    onClose={() => setDialogState(null)}
                    uzivatelId={dialogState.uzivatelId}
                    uzivatelJmeno={dialogState.uzivatelJmeno}
                    kontrolaStatus={dialogState.kontrolaStatus}
                />
            )}
        </Box>
    );
};

// ==========================================
// DIALOG: Typ kontroly add/edit
// ==========================================

interface TypKontrolyFormData {
    kod: string;
    nazev: string;
    popis: string;
    periodaMesicu: number;
    varovaniDnu: number;
    funkceKod: string;
    specializaceKod: string;
    aktivni: boolean;
    zobrazitDashboard: boolean;
    poradi: number;
    target: 'UZIVATEL' | 'TECHNIKA' | 'INVENTAR';
    metadataFields: MetadataFieldSchema[];
    notifikaceIntervaly: number[];
    notifikacePrijemci: 'VSICHNI' | 'FUNKCE' | 'NIKDO';
    lzePlnitUcasti: boolean;
    emailEnabled: boolean;
    emailIntervaly: number[];
    emailPredmet: string;
    emailTelo: string;
    nahrazuje: NahrazeniRequest[];
}

const emptyTypForm: TypKontrolyFormData = {
    kod: '',
    nazev: '',
    popis: '',
    periodaMesicu: 12,
    varovaniDnu: 30,
    funkceKod: '',
    specializaceKod: '',
    aktivni: true,
    zobrazitDashboard: true,
    poradi: 0,
    target: 'UZIVATEL',
    metadataFields: [],
    notifikaceIntervaly: [],
    notifikacePrijemci: 'VSICHNI',
    lzePlnitUcasti: false,
    emailEnabled: false,
    emailIntervaly: [],
    emailPredmet: '',
    emailTelo: '',
    nahrazuje: [],
};

const TypKontrolyDialog: React.FC<{
    open: boolean;
    onClose: () => void;
    editingTyp: TypKontroly | null;
}> = ({ open, onClose, editingTyp }) => {
    const [form, setForm] = useState<TypKontrolyFormData>(emptyTypForm);
    const [newInterval, setNewInterval] = useState('');
    const [dialogTab, setDialogTab] = useState(0);

    // HO-22: gating e-mailových záložek přes MODUL_EMAIL
    const { enabled: emailFlagEnabled } = useFeatureFlag('MODUL_EMAIL');

    const createMutation = useCreateTypKontrolyMutation();
    const updateMutation = useUpdateTypKontrolyMutation();
    const { data: allSpecializace } = useAllSpecializaceQuery();
    const { data: vsechnyTypy } = useTypyKontrolQuery();
    const [chybaUlozeni, setChybaUlozeni] = useState<string | null>(null);

    // Chyba ulozeni patri k odeslane verzi formulare; po jeho zmene je zastarala
    useEffect(() => {
        setChybaUlozeni(null);
    }, [form]);

    const FUNKCE_OPTIONS = [
        { kod: 'VD', nazev: 'Velitel družstva' },
        { kod: 'S', nazev: 'Strojník' },
        { kod: 'H', nazev: 'Hasič' },
    ];

    // Fallback: pokud je emailFlag OFF a dialog je otevřený na e-mailovém tabu, vrátit na 0
    React.useEffect(() => {
        if (!emailFlagEnabled && (dialogTab === 2 || dialogTab === 3)) {
            setDialogTab(0);
        }
    }, [emailFlagEnabled, dialogTab]);

    React.useEffect(() => {
        setDialogTab(0);
        setNewInterval('');
        setChybaUlozeni(null);
        if (editingTyp) {
            let metadataFields: MetadataFieldSchema[] = [];
            if (editingTyp.metadataSchema) {
                try {
                    metadataFields = JSON.parse(editingTyp.metadataSchema);
                } catch { /* ignore */ }
            }
            const notifikaceIntervaly = parsujIntervaly(editingTyp.notifikaceIntervaly);
            setForm({
                kod: editingTyp.kod,
                nazev: editingTyp.nazev,
                popis: editingTyp.popis || '',
                periodaMesicu: editingTyp.periodaMesicu,
                varovaniDnu: editingTyp.varovaniDnu,
                funkceKod: editingTyp.funkceKod || '',
                specializaceKod: editingTyp.specializaceKod || '',
                aktivni: editingTyp.aktivni,
                zobrazitDashboard: editingTyp.zobrazitDashboard,
                poradi: editingTyp.poradi,
                target: editingTyp.target || 'UZIVATEL',
                metadataFields,
                notifikaceIntervaly,
                notifikacePrijemci: (editingTyp.notifikacePrijemci as 'VSICHNI' | 'FUNKCE' | 'NIKDO') || 'VSICHNI',
                lzePlnitUcasti: editingTyp.lzePlnitUcasti ?? false,
                emailEnabled: editingTyp.emailEnabled ?? false,
                emailIntervaly: (() => {
                    try { return editingTyp.emailIntervaly ? JSON.parse(editingTyp.emailIntervaly) : []; }
                    catch { return []; }
                })(),
                emailPredmet: editingTyp.emailPredmet || '',
                emailTelo: editingTyp.emailTelo || '',
                nahrazuje: (editingTyp.nahrazuje ?? [])
                    .filter((n) => n.typ.typId !== null)
                    .map((n) => ({ nahrazenyTypId: n.typ.typId as number, uznatZaznam: n.uznatZaznam })),
            });
        } else {
            setForm(emptyTypForm);
        }
    }, [editingTyp, open]);

    // Zivy nahled (spec 6.1 bod 3): cely neulozeny stav, ktery ovlivnuje vysledek; jen u kontrol clenu
    const nahledRequest: NahledTypKontrolyRequest | null = useMemo(() => {
        if (!open || form.target !== 'UZIVATEL') return null;
        return {
            id: editingTyp?.id ?? null,
            kod: form.kod,
            nazev: form.nazev,
            poradi: form.poradi,
            target: form.target,
            funkceKod: form.funkceKod || null,
            specializaceKod: form.specializaceKod || null,
            periodaMesicu: form.periodaMesicu,
            varovaniDnu: form.varovaniDnu,
            aktivni: form.aktivni,
            zobrazitDashboard: form.zobrazitDashboard,
            nahrazuje: form.nahrazuje,
        };
    }, [open, editingTyp?.id, form.kod, form.nazev, form.poradi, form.target, form.funkceKod,
        form.specializaceKod, form.periodaMesicu, form.varovaniDnu, form.aktivni, form.zobrazitDashboard,
        form.nahrazuje]);
    const nahled = useNahledTypKontrolyQuery(nahledRequest);

    const handleSave = () => {
        setChybaUlozeni(null);
        const metadataSchema = form.metadataFields.length > 0
            ? JSON.stringify(form.metadataFields)
            : undefined;

        const request: TypKontrolyRequest = {
            kod: form.kod,
            nazev: form.nazev,
            popis: form.popis || undefined,
            periodaMesicu: form.periodaMesicu,
            varovaniDnu: form.varovaniDnu,
            funkceKod: form.funkceKod || undefined,
            specializaceKod: form.specializaceKod || undefined,
            metadataSchema,
            aktivni: form.aktivni,
            zobrazitDashboard: form.zobrazitDashboard,
            poradi: form.poradi,
            target: form.target,
            notifikaceIntervaly: maPushUpozorneni(form)
                ? JSON.stringify([...form.notifikaceIntervaly].sort((a, b) => b - a))
                : undefined,
            notifikacePrijemci: form.notifikacePrijemci,
            lzePlnitUcasti: form.lzePlnitUcasti,
            emailEnabled: form.emailEnabled,
            emailIntervaly: form.emailIntervaly.length > 0
                ? JSON.stringify([...form.emailIntervaly].sort((a, b) => b - a))
                : undefined,
            emailPredmet: form.emailPredmet || undefined,
            emailTelo: form.emailTelo || undefined,
            // PUT posila vzdy cele pole (spec 6.1 bod 4); jiny cil nez UZIVATEL nic nenahrazuje
            nahrazuje: form.target === 'UZIVATEL' ? form.nahrazuje : [],
        };

        if (editingTyp) {
            updateMutation.mutate({ id: editingTyp.id, data: request }, {
                onSuccess: () => onClose(),
                onError: (e) => setChybaUlozeni(e.message),
            });
        } else {
            createMutation.mutate(request, {
                onSuccess: () => onClose(),
                onError: (e) => setChybaUlozeni(e.message),
            });
        }
    };

    const addMetadataField = () => {
        setForm({
            ...form,
            metadataFields: [...form.metadataFields, { key: '', label: '', type: 'text' }],
        });
    };

    const removeMetadataField = (index: number) => {
        setForm({
            ...form,
            metadataFields: form.metadataFields.filter((_, i) => i !== index),
        });
    };

    const updateMetadataField = (index: number, field: Partial<MetadataFieldSchema>) => {
        const updated = [...form.metadataFields];
        updated[index] = { ...updated[index], ...field };
        setForm({ ...form, metadataFields: updated });
    };

    const isSaving = createMutation.isPending || updateMutation.isPending;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {editingTyp ? 'Upravit typ kontroly' : 'Nový typ kontroly'}
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
                <Tabs
                    value={dialogTab}
                    onChange={(_, v) => setDialogTab(v)}
                    sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
                >
                    <Tab label="Základní" />
                    <Tab label="Notifikace" />
                    {emailFlagEnabled && <Tab label="E-mail notifikace" />}
                    {emailFlagEnabled && <Tab label="Dokumenty" />}
                </Tabs>

                <Box sx={{ p: 2 }}>
                    {/* TAB 0 — Základní */}
                    {dialogTab === 0 && (
                        <>
                            <TextField
                                label="Kód"
                                value={form.kod}
                                onChange={(e) => setForm({ ...form, kod: e.target.value.toUpperCase() })}
                                fullWidth
                                margin="dense"
                                size="small"
                                required
                                helperText="Unikátní kód (např. ZDRAVOTNI)"
                            />
                            <TextField
                                label="Název"
                                value={form.nazev}
                                onChange={(e) => setForm({ ...form, nazev: e.target.value })}
                                fullWidth
                                margin="dense"
                                size="small"
                                required
                            />
                            <TextField
                                label="Popis"
                                value={form.popis}
                                onChange={(e) => setForm({ ...form, popis: e.target.value })}
                                fullWidth
                                margin="dense"
                                size="small"
                                multiline
                                rows={2}
                            />
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                    label="Perioda (měsíce)"
                                    type="number"
                                    value={form.periodaMesicu}
                                    onChange={(e) => setForm({ ...form, periodaMesicu: Number(e.target.value) })}
                                    margin="dense"
                                    size="small"
                                    required
                                    sx={{ flex: 1 }}
                                />
                                <TextField
                                    label="Varování (dní)"
                                    type="number"
                                    value={form.varovaniDnu}
                                    onChange={(e) => setForm({ ...form, varovaniDnu: Number(e.target.value) })}
                                    margin="dense"
                                    size="small"
                                    sx={{ flex: 1 }}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <FormControl fullWidth margin="dense" size="small">
                                    <InputLabel>Funkce (omezení)</InputLabel>
                                    <Select
                                        value={form.funkceKod}
                                        label="Funkce (omezení)"
                                        onChange={(e) => setForm({ ...form, funkceKod: e.target.value })}
                                    >
                                        <MenuItem value="">Všichni</MenuItem>
                                        {FUNKCE_OPTIONS.map((f) => (
                                            <MenuItem key={f.kod} value={f.kod}>{f.nazev} ({f.kod})</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl fullWidth margin="dense" size="small">
                                    <InputLabel>Specializace (omezení)</InputLabel>
                                    <Select
                                        value={form.specializaceKod}
                                        label="Specializace (omezení)"
                                        onChange={(e) => setForm({ ...form, specializaceKod: e.target.value })}
                                    >
                                        <MenuItem value="">Všichni</MenuItem>
                                        {(allSpecializace || []).map((spec) => (
                                            <MenuItem key={spec.kod} value={spec.kod}>{spec.nazev} ({spec.kod})</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                            <FormControl fullWidth margin="dense" size="small">
                                <InputLabel>Cíl kontroly</InputLabel>
                                <Select
                                    value={form.target}
                                    label="Cíl kontroly"
                                    onChange={(e) => setForm({ ...form, target: e.target.value as 'UZIVATEL' | 'TECHNIKA' | 'INVENTAR' })}
                                >
                                    <MenuItem value="UZIVATEL">Uživatel</MenuItem>
                                    <MenuItem value="TECHNIKA">Technika (vozidlo)</MenuItem>
                                    <MenuItem value="INVENTAR">Inventář (OOP)</MenuItem>
                                </Select>
                            </FormControl>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={form.aktivni}
                                            onChange={(e) => setForm({ ...form, aktivni: e.target.checked })}
                                        />
                                    }
                                    label="Aktivní"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={form.zobrazitDashboard}
                                            onChange={(e) => setForm({ ...form, zobrazitDashboard: e.target.checked })}
                                        />
                                    }
                                    label="Upozornění na dashboardu"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={!!form.lzePlnitUcasti}
                                            onChange={(e) => setForm({ ...form, lzePlnitUcasti: e.target.checked })}
                                        />
                                    }
                                    label="Lze splnit účastí na akci"
                                />
                                <TextField
                                    label="Pořadí"
                                    type="number"
                                    value={form.poradi}
                                    onChange={(e) => setForm({ ...form, poradi: Number(e.target.value) })}
                                    size="small"
                                    sx={{ width: 100 }}
                                />
                            </Box>

                            {form.target === 'UZIVATEL' && (
                                <>
                                    <NahrazujeKontrolyPole
                                        editovanyTypId={editingTyp?.id ?? null}
                                        maPodminku={!!form.funkceKod || !!form.specializaceKod}
                                        typy={vsechnyTypy ?? []}
                                        value={form.nahrazuje}
                                        onChange={(nahrazuje) => setForm({ ...form, nahrazuje })}
                                        maPushUpozorneni={maPushUpozorneni(form)}
                                        maEmailUpozorneni={form.emailEnabled}
                                    />
                                    <NahledTypKontrolyPanel
                                        data={nahled.data}
                                        isLoading={nahled.isLoading}
                                        isError={nahled.isError}
                                        isPlaceholder={nahled.isPlaceholderData}
                                    />
                                </>
                            )}

                            {/* Metadata schéma editor */}
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Rozšířená pole formuláře
                                </Typography>
                                {form.metadataFields.map((field, index) => (
                                    <Box key={index} sx={{ mb: 1 }}>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                            <TextField
                                                label="Klíč"
                                                value={field.key}
                                                onChange={(e) => updateMetadataField(index, { key: e.target.value })}
                                                size="small"
                                                sx={{ width: 120 }}
                                            />
                                            <TextField
                                                label="Popisek"
                                                value={field.label}
                                                onChange={(e) => updateMetadataField(index, { label: e.target.value })}
                                                size="small"
                                                sx={{ flex: 1 }}
                                            />
                                            <FormControl size="small" sx={{ width: 120 }}>
                                                <Select
                                                    value={field.type}
                                                    onChange={(e) => updateMetadataField(index, { type: e.target.value as any })}
                                                >
                                                    <MenuItem value="text">Text</MenuItem>
                                                    <MenuItem value="number">Číslo</MenuItem>
                                                    <MenuItem value="technika">Vozidlo</MenuItem>
                                                    <MenuItem value="select">Výběr</MenuItem>
                                                </Select>
                                            </FormControl>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={field.required || false}
                                                        onChange={(e) => updateMetadataField(index, { required: e.target.checked })}
                                                        size="small"
                                                    />
                                                }
                                                label="Pov."
                                            />
                                            <IconButton size="small" onClick={() => removeMetadataField(index)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                        {field.type === 'select' && (
                                            <Box sx={{ ml: 2, mt: 0.5 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Volby (hodnota:popisek, oddělené čárkou)
                                                </Typography>
                                                <TextField
                                                    value={(field.options || []).map(o => `${o.value}:${o.label}`).join(', ')}
                                                    onChange={(e) => {
                                                        const optionsStr = e.target.value;
                                                        const options = optionsStr.split(',')
                                                            .map(s => s.trim())
                                                            .filter(s => s.includes(':'))
                                                            .map(s => {
                                                                const [value, ...rest] = s.split(':');
                                                                return { value: value.trim(), label: rest.join(':').trim() };
                                                            });
                                                        updateMetadataField(index, { options });
                                                    }}
                                                    placeholder="ano:Ano, ne:Ne, mozna:Možná"
                                                    fullWidth
                                                    size="small"
                                                    margin="dense"
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                ))}
                                <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={addMetadataField}
                                >
                                    Přidat pole
                                </Button>
                            </Box>
                        </>
                    )}

                    {/* TAB 1 — Notifikace */}
                    {dialogTab === 1 && (
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Notifikace o expiraci
                            </Typography>
                            <FormControl fullWidth margin="dense" size="small">
                                <InputLabel>Příjemci notifikací</InputLabel>
                                <Select
                                    value={form.notifikacePrijemci}
                                    label="Příjemci notifikací"
                                    onChange={(e) => setForm({ ...form, notifikacePrijemci: e.target.value as 'VSICHNI' | 'FUNKCE' | 'NIKDO' })}
                                >
                                    <MenuItem value="VSICHNI">Všem členům</MenuItem>
                                    <MenuItem value="FUNKCE">Jen dle funkce{form.funkceKod ? ` (${form.funkceKod})` : ''} + adminům</MenuItem>
                                    <MenuItem value="NIKDO">Vypnuto</MenuItem>
                                </Select>
                            </FormControl>

                            {form.notifikacePrijemci !== 'NIKDO' && (
                                <>
                                    {form.notifikacePrijemci === 'FUNKCE' && form.funkceKod && (
                                        <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
                                            Notifikace půjde členům s funkcí {form.funkceKod} + adminům
                                        </Alert>
                                    )}
                                    <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                                            Intervaly (dní před expirací):
                                        </Typography>
                                        {[...form.notifikaceIntervaly].sort((a, b) => b - a).map((den) => (
                                            <Chip
                                                key={den}
                                                label={den === 0 ? 'Den expirace' : `${den} dní`}
                                                onDelete={() => setForm({
                                                    ...form,
                                                    notifikaceIntervaly: form.notifikaceIntervaly.filter((d) => d !== den),
                                                })}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        ))}
                                    </Box>
                                    <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <TextField
                                            label="Dní"
                                            type="number"
                                            size="small"
                                            sx={{ width: 100 }}
                                            inputProps={{ min: 0 }}
                                            value={newInterval}
                                            onChange={(e) => setNewInterval(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    const val = parseInt(newInterval);
                                                    if (!isNaN(val) && val >= 0 && !form.notifikaceIntervaly.includes(val)) {
                                                        setForm({
                                                            ...form,
                                                            notifikaceIntervaly: [...form.notifikaceIntervaly, val],
                                                        });
                                                        setNewInterval('');
                                                    }
                                                }
                                            }}
                                        />
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => {
                                                const val = parseInt(newInterval);
                                                if (!isNaN(val) && val >= 0 && !form.notifikaceIntervaly.includes(val)) {
                                                    setForm({
                                                        ...form,
                                                        notifikaceIntervaly: [...form.notifikaceIntervaly, val],
                                                    });
                                                    setNewInterval('');
                                                }
                                            }}
                                        >
                                            Přidat
                                        </Button>
                                    </Box>
                                </>
                            )}
                        </Box>
                    )}

                    {/* TAB 2 — E-mail notifikace (jen když MODUL_EMAIL zapnutý) */}
                    {dialogTab === 2 && emailFlagEnabled && (
                        <EmailNotifikaceTab
                            emailEnabled={form.emailEnabled}
                            emailIntervaly={form.emailIntervaly}
                            emailPredmet={form.emailPredmet}
                            emailTelo={form.emailTelo}
                            onEmailEnabledChange={(val) => setForm({ ...form, emailEnabled: val })}
                            onEmailIntervalyChange={(val) => setForm({ ...form, emailIntervaly: val })}
                            onEmailPredmetChange={(val) => setForm({ ...form, emailPredmet: val })}
                            onEmailTeloChange={(val) => setForm({ ...form, emailTelo: val })}
                        />
                    )}

                    {/* TAB 3 — Dokumenty (jen když MODUL_EMAIL zapnutý) */}
                    {dialogTab === 3 && emailFlagEnabled && (
                        <DokumentyTab typId={editingTyp?.id ?? null} />
                    )}
                </Box>
                {chybaUlozeni && (
                    <Alert severity="error" sx={{ mx: 2, mb: 2 }}>{chybaUlozeni}</Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Zrušit</Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!form.kod || !form.nazev || !form.periodaMesicu || isSaving}
                >
                    {isSaving ? <CircularProgress size={20} /> : editingTyp ? 'Uložit' : 'Vytvořit'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ==========================================
// TAB 2: Typy kontrol (CRUD)
// ==========================================

const TypyKontrolTab: React.FC = () => {
    const { data: typy, isLoading, error } = useTypyKontrolQuery();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingTyp, setEditingTyp] = useState<TypKontroly | null>(null);
    const [deleteTyp, setDeleteTyp] = useState<TypKontroly | null>(null);

    if (isLoading) return <CircularProgress />;
    if (error) return <Alert severity="error">Nepodařilo se načíst typy kontrol.</Alert>;

    return (
        <Box>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setEditingTyp(null);
                        setDialogOpen(true);
                    }}
                >
                    Nový typ
                </Button>
            </Box>

            {!typy?.length ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    Žádné typy kontrol
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {typy.map((typ) => (
                        <Paper key={typ.id} variant="outlined" sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <Typography variant="subtitle1" fontWeight={700}>{typ.nazev}</Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                    <Tooltip title="Upravit">
                                        <IconButton size="small" onClick={() => { setEditingTyp(typ); setDialogOpen(true); }}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Smazat">
                                        <IconButton size="small" onClick={() => setDeleteTyp(typ)}>
                                            <DeleteIcon fontSize="small" color="error" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                                <Chip
                                    label={typ.target === 'TECHNIKA' ? 'Technika' : typ.target === 'INVENTAR' ? 'Inventář' : 'Uživatel'}
                                    color={typ.target === 'TECHNIKA' ? 'info' : typ.target === 'INVENTAR' ? 'warning' : 'default'}
                                    size="small"
                                    variant="outlined"
                                />
                                <Chip
                                    label={typ.aktivni ? 'Aktivní' : 'Neaktivní'}
                                    color={typ.aktivni ? 'success' : 'default'}
                                    size="small"
                                />
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'auto 1fr' }, gap: '2px 12px', mt: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">Platnost</Typography>
                                <Typography variant="caption">{typ.periodaMesicu} měsíců</Typography>
                                <Typography variant="caption" color="text.secondary">Upozornění</Typography>
                                <Typography variant="caption">{typ.varovaniDnu} dní před vypršením</Typography>
                                {typ.funkceKod && (<>
                                    <Typography variant="caption" color="text.secondary">Povinné pro</Typography>
                                    <Typography variant="caption">{typ.funkceKod}</Typography>
                                </>)}
                                {typ.specializaceKod && (<>
                                    <Typography variant="caption" color="text.secondary">Specializace</Typography>
                                    <Typography variant="caption">{typ.specializaceKod}</Typography>
                                </>)}
                                {typ.zobrazitDashboard && (<>
                                    <Typography variant="caption" color="text.secondary">Dashboard</Typography>
                                    <Typography variant="caption">Ano</Typography>
                                </>)}
                            </Box>
                        </Paper>
                    ))}
                </Box>
            )}

            <TypKontrolyDialog
                open={dialogOpen}
                onClose={() => {
                    setDialogOpen(false);
                    setEditingTyp(null);
                }}
                editingTyp={editingTyp}
            />

            <SmazatTypKontrolyDialog typ={deleteTyp} onClose={() => setDeleteTyp(null)} />
        </Box>
    );
};

// ==========================================
// MAIN PAGE
// ==========================================

const KontrolyPage: React.FC = () => {
    const [tabIndex, setTabIndex] = useState(0);

    return (
        <Box>
            <PageHeader title="Kontroly a povinnosti" />

            <Paper sx={{ mb: 2 }}>
                <Tabs
                    value={tabIndex}
                    onChange={(_, newValue) => setTabIndex(newValue)}
                    variant="fullWidth"
                >
                    <Tab label="Přehled" />
                    <Tab label="Typy kontrol" />
                </Tabs>
            </Paper>

            {tabIndex === 0 && <PrehledTab />}
            {tabIndex === 1 && <TypyKontrolTab />}
        </Box>
    );
};

export default KontrolyPage;
